import { logger } from '@parrot-software/observability';
import { context, trace } from '@opentelemetry/api';
import type { RetryInvoiceDataDto } from '@subscriptions/application/dtos';
import { createRetryInvoiceError, isRetryInvoiceError } from '@subscriptions/application/errors';
import type { IInvoiceDataApiPort } from '@subscriptions/application/ports';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/** Status a CFDI comes back with when the PAC rejected it. */
const FAILED_STATUS = 'FAILED';

/**
 * Use case for re-invoicing a payment.
 *
 * The endpoint cancels the live CFDIs at the SAT, drops their records and stamps the new ones on
 * its own, so the only input is how much of the payment it touches. `onlyFailed` narrows it to
 * the CFDIs that failed, which is what a per-store row needs: re-issuing the whole payment would
 * cancel its siblings' valid CFDIs along the way. It defaults to false, so a payment with a
 * single invoice is re-issued as a whole.
 *
 * @param port - IInvoiceDataApiPort implementation (injected by composition)
 * @returns Use case executor
 *
 * @example
 * const useCase = retryInvoiceDataUseCase(repository);
 * const result = await useCase.execute('3fa85f64-5717-4562-b3fc-2c963f66afa6', true);
 */
export const retryInvoiceDataUseCase = (port: IInvoiceDataApiPort) => {
  const execute = async (
    paymentUuid: string,
    onlyFailed = false,
    includeDeleted = false,
  ): Promise<RetryInvoiceDataDto> => {
    const span = tracer.startSpan('SUB.retryInvoiceData', {
      attributes: { 'invoice.paymentUuid': paymentUuid, 'invoice.onlyFailed': onlyFailed },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const result = await port.retry(paymentUuid, { onlyFailed, includeDeleted });
        // The request resolving only means the endpoint ran: it stamps inside the request and
        // answers 200 either way, so the verdict is per invoice. A rejected CFDI is turned into
        // a rejection here, once, instead of leaving every caller to remember to look.
        const rejected = result.invoices.filter((invoice) => invoice.status === FAILED_STATUS);

        if (rejected.length > 0) {
          throw createRetryInvoiceError(
            rejected.map((invoice) => invoice.errorMessage).filter((reason): reason is string => Boolean(reason)),
          );
        }

        invoiceLog.info('retryInvoiceData', {
          'invoice.paymentUuid': paymentUuid,
          'invoice.onlyFailed': onlyFailed,
          'invoice.priorInvoices': result.priorInvoices,
          'invoice.canceledInvoices': result.canceled,
          'invoice.deletedInvoices': result.deleted,
          'invoice.issuedInvoices': result.invoices.length,
        });
        return result;
      } catch (error) {
        // The PAC's reasons are deliberately absent: they quote the taxpayer's RFC (Charter §5.1).
        // How many CFDIs were rejected is the part that is safe and still actionable.
        invoiceLog.error('retryInvoiceData', {
          ...extractErrorAttributes(error),
          'invoice.paymentUuid': paymentUuid,
          'invoice.onlyFailed': onlyFailed,
          ...(isRetryInvoiceError(error) ? { 'invoice.rejectedInvoices': error.reasons.length } : {}),
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
